import React from 'react';
import classNames from 'classnames';
import { InputMask } from '@react-input/mask';
import styles from './Input.module.scss';

const CENTS_STEP_VALUE = '.01';

interface InputProps {
  id?: string;
  label: string;
  value?: string | number;
  name: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  placeholder?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  className?: string;
  containerClassName?: string;
  required?: boolean;
  propRef?: any;
  type?: string;
  characterLimit?: number;
  error?: string;
  datatestid?: string;
  dataRecurly?: string;
  inputMask?: string;
}

const Input = React.forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps>(
  (
    {
      id,
      label,
      value,
      name,
      onChange,
      placeholder,
      disabled,
      style,
      className,
      required,
      type,
      characterLimit,
      error,
      datatestid,
      dataRecurly,
      inputMask,
      onBlur,
      containerClassName,
    },
    ref,
  ) => {
    const resolveInputByType = () => {
      switch (type) {
        case 'text': {
          return (
            <input
              id={id}
              className={classNames(className, styles.input, { [styles.error]: error })}
              data-testid={datatestid}
              name={name}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              type="text"
              required={required}
              disabled={disabled}
              maxLength={characterLimit}
              ref={ref as React.LegacyRef<HTMLInputElement>}
              data-recurly={dataRecurly}
              onBlur={onBlur}
            />
          );
        }
        case 'phone': {
          return (
            <InputMask
              id={id}
              className={classNames(className, styles.input, { [styles.error]: error })}
              data-testid={datatestid}
              name={name}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              type="text"
              required={required}
              disabled={disabled}
              maxLength={characterLimit}
              ref={ref as React.LegacyRef<HTMLInputElement>}
              data-recurly={dataRecurly}
              mask={inputMask}
              key={inputMask}
              replacement={{ _: /\d/ }}
              onBlur={onBlur}
            />
          );
        }
        case 'textArea': {
          return (
            <textarea
              id={id}
              className={classNames(className, styles.input, styles.textArea, {
                [styles.error]: error,
              })}
              name={name}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              disabled={disabled}
              required={required}
              maxLength={characterLimit}
              onBlur={onBlur}
            />
          );
        }
        case 'price': {
          return (
            <div className={styles.priceInput}>
              <i className="far fa-dollar-sign" />
              <input
                id={id}
                className={classNames(className, styles.input, { [styles.error]: error })}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                type="number"
                step={CENTS_STEP_VALUE}
                min="0"
                pattern="^\d*(\.\d{0,2})?$"
                required={required}
                disabled={disabled}
                maxLength={characterLimit}
                ref={ref as React.LegacyRef<HTMLInputElement>}
                onBlur={onBlur}
              />
            </div>
          );
        }

        default: {
          return (
            <input
              id={id}
              className={classNames(className, styles.input, { [styles.error]: error })}
              name={name}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              type={type}
              required={required}
              disabled={disabled}
              maxLength={characterLimit}
              ref={ref as React.LegacyRef<HTMLInputElement>}
              onBlur={onBlur}
            />
          );
        }
      }
    };

    return (
      <div className={classNames(styles.container, containerClassName)} style={style}>
        {label && <label htmlFor={id || name}>{label}</label>}
        {resolveInputByType()}
        {/*color attribute error must be handled separately due to design of input*/}
        {error && label !== 'Color Attribute' && (
          <p className={styles.errorMessage} data-testid={`${label}-error`}>
            {error}
          </p>
        )}
      </div>
    );
  },
);

export default Input;
